/**
 * DealActionMenu — Kebab dropdown for deal lifecycle actions.
 * Supports rename, pause/resume, close won/lost.
 */

import { useState, useRef, useEffect } from "react";
import type { DealData } from "@/api/client";

interface DealActionMenuProps {
  deal: DealData;
  onUpdate: (updated: DealData) => void;
  onClose: () => void;
}

function KebabIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor">
      <circle cx="8" cy="3" r="1.5" />
      <circle cx="8" cy="8" r="1.5" />
      <circle cx="8" cy="13" r="1.5" />
    </svg>
  );
}

export function DealActionMenuTrigger({
  onClick,
}: {
  onClick: (e: React.MouseEvent) => void;
}) {
  return (
    <button
      className="deal-kebab-btn"
      onClick={(e) => {
        e.stopPropagation();
        onClick(e);
      }}
      aria-label="Deal actions"
    >
      <KebabIcon />
    </button>
  );
}

export default function DealActionMenu({ deal, onUpdate, onClose }: DealActionMenuProps) {
  const [renaming, setRenaming] = useState(false);
  const [newTitle, setNewTitle] = useState(deal.title || deal.address || "");
  const [loading, setLoading] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  // Dismiss on outside click
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        onClose();
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [onClose]);

  // Focus input when entering rename mode
  useEffect(() => {
    if (renaming) inputRef.current?.focus();
  }, [renaming]);

  const doUpdate = async (fields: Record<string, string>) => {
    setLoading(true);
    try {
      const { updateDeal } = await import("@/api/client");
      const updated = await updateDeal(deal.id, fields);
      if (updated) onUpdate(updated);
    } finally {
      setLoading(false);
      onClose();
    }
  };

  const handleRename = () => {
    const trimmed = newTitle.trim();
    if (trimmed && trimmed !== (deal.title || deal.address)) {
      doUpdate({ title: trimmed });
    } else {
      setRenaming(false);
    }
  };

  const canPause = ["screening", "active", "diligence"].includes(deal.status);
  const canResume = deal.status === "paused";
  const canClose = !["closed_won", "closed_lost"].includes(deal.status);

  return (
    <div className="deal-action-menu" ref={menuRef}>
      {renaming ? (
        <div className="deal-action-rename">
          <input
            ref={inputRef}
            className="deal-action-rename-input"
            value={newTitle}
            onChange={(e) => setNewTitle(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") handleRename();
              if (e.key === "Escape") setRenaming(false);
            }}
            disabled={loading}
          />
          <button className="deal-action-rename-save" onClick={handleRename} disabled={loading}>
            Save
          </button>
        </div>
      ) : (
        <>
          <button className="deal-action-item" onClick={() => setRenaming(true)} disabled={loading}>
            Rename
          </button>
          {canPause && (
            <button className="deal-action-item" onClick={() => doUpdate({ status: "paused" })} disabled={loading}>
              Pause
            </button>
          )}
          {canResume && (
            <button className="deal-action-item" onClick={() => doUpdate({ status: "active" })} disabled={loading}>
              Resume
            </button>
          )}
          {canClose && (
            <>
              <div className="deal-action-divider" />
              <button
                className="deal-action-item deal-action-item--success"
                onClick={() => doUpdate({ status: "closed_won" })}
                disabled={loading}
              >
                Close Won
              </button>
              <button
                className="deal-action-item deal-action-item--danger"
                onClick={() => doUpdate({ status: "closed_lost" })}
                disabled={loading}
              >
                Close Lost
              </button>
            </>
          )}
        </>
      )}
    </div>
  );
}
